// state/integrations.js
import integrationAPI from '@api/integration'
import getErrorMessage from '@utils/getErrorMessage'

import {
  INTEGRATION_LIST_BEGIN,
  INTEGRATION_LIST_SUCCESS,
  INTEGRATION_LIST_FAILURE,
  INTEGRATION_CREATE_BEGIN,
  INTEGRATION_CREATE_SUCCESS,
  INTEGRATION_CREATE_FAILURE,
  INTEGRATION_UPDATE_BEGIN,
  INTEGRATION_UPDATE_SUCCESS,
  INTEGRATION_UPDATE_FAILURE,
  INTEGRATION_DELETE_BEGIN,
  INTEGRATION_DELETE_SUCCESS,
  INTEGRATION_DELETE_FAILURE,
  SET_TARGET_INTEGRATION,
  INTEGRATION_CLEAR,
} from './_types'

const getDefaultState = () => ({
  targetIntegrationID: '',
  integrations: [],
  integrationActionCompleted: false,
  integrationActionError: false,
  integrationActionLoading: false,
  integrationErrorMessage: '',
})

export const state = getDefaultState()

export const getters = {
  /**
   * Returns the target integration
   */
  targetIntegration: (state) => {
    if (!state.targetIntegrationID) return null
    return state.integrations.find(
      (integration) => integration.id === state.targetIntegrationID,
    ) || null
  },
  /**
   * Checks if a specific integration exists
   * @param {Object} state
   * @returns {Function} A function that takes the target integration name as a parameter
   */
  isIntegrationPresent: (state) => (target) => {
    return state.integrations.some(
      (integration) => integration.target === target,
    )
  },
}

export const mutations = {
  [SET_TARGET_INTEGRATION](state, uuid) {
    state.targetIntegrationID = uuid
  },
  [INTEGRATION_LIST_BEGIN](state) {
    state.integrationActionCompleted = false
    state.integrationActionLoading = true
    state.integrationActionError = false
  },
  [INTEGRATION_LIST_SUCCESS](state, integrations) {
    state.integrations = integrations
    state.integrationActionCompleted = true
    state.integrationActionLoading = false
  },
  [INTEGRATION_LIST_FAILURE](state, message) {
    state.integrationActionError = true
    state.integrationActionLoading = false
    state.integrationActionCompleted = true
    state.integrationErrorMessage = message
  },
  [INTEGRATION_CREATE_BEGIN](state) {
    state.integrationActionLoading = true
    state.integrationActionError = false
    state.integrationActionCompleted = false
  },
  [INTEGRATION_CREATE_SUCCESS](state, integration) {
    state.integrations.push(integration)
    state.integrationActionCompleted = true
    state.integrationActionLoading = false
  },
  [INTEGRATION_CREATE_FAILURE](state, message) {
    state.integrationActionError = true
    state.integrationActionLoading = false
    state.integrationActionCompleted = true
    state.integrationErrorMessage = message
  },
  [INTEGRATION_UPDATE_BEGIN](state) {
    state.integrationActionLoading = true
    state.integrationActionError = false
    state.integrationActionCompleted = false
  },
  [INTEGRATION_UPDATE_SUCCESS](state, updatedIntegration) {
    const index = state.integrations.findIndex(
      (i) => i.id === state.targetIntegrationID,
    )
    if (index !== -1) {
      state.integrations.splice(index, 1, updatedIntegration)
    }
    state.integrationActionCompleted = true
    state.integrationActionLoading = false
  },
  [INTEGRATION_UPDATE_FAILURE](state, message) {
    state.integrationActionError = true
    state.integrationActionLoading = false
    state.integrationActionCompleted = true
    state.integrationErrorMessage = message
  },
  [INTEGRATION_DELETE_BEGIN](state) {
    state.integrationActionLoading = true
    state.integrationActionError = false
    state.integrationActionCompleted = false
  },
  [INTEGRATION_DELETE_SUCCESS](state, deletedIntegrationId) {
    state.integrations = state.integrations.filter(
      (integration) => integration.id !== deletedIntegrationId,
    )
    state.integrationActionCompleted = true
    state.integrationActionLoading = false
  },
  [INTEGRATION_DELETE_FAILURE](state, message) {
    state.integrationActionError = true
    state.integrationActionLoading = false
    state.integrationActionCompleted = true
    state.integrationErrorMessage = message
  },
  [INTEGRATION_CLEAR](state) {
    Object.assign(state, getDefaultState())
  },
}

export const actions = {
  listIntegrations({ commit, rootState }) {
    commit(INTEGRATION_LIST_BEGIN)
    const targetWidgetID = rootState.campaign.widget.targetWidgetID

    return integrationAPI
      .listIntegrations(targetWidgetID)
      .then((response) => {
        commit(INTEGRATION_LIST_SUCCESS, response.data)
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        commit(INTEGRATION_LIST_FAILURE, errorMessage)
        return Promise.reject(error)
      })
  },
  createIntegration({ commit }, integrationData) {
    commit(INTEGRATION_CREATE_BEGIN)
    return integrationAPI
      .createIntegration(integrationData)
      .then((response) => {
        commit(SET_TARGET_INTEGRATION, response.data.id)
        commit(INTEGRATION_CREATE_SUCCESS, response.data)
        return response.data.id
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        commit(INTEGRATION_CREATE_FAILURE, errorMessage)
        return Promise.reject(error)
      })
  },
  updateIntegration({ commit, state }, integrationData) {
    commit(INTEGRATION_UPDATE_BEGIN)

    // Include the targetIntegrationID in the integrationData
    integrationData.id = state.targetIntegrationID

    return integrationAPI
      .updateIntegration(integrationData)
      .then((response) => {
        commit(INTEGRATION_UPDATE_SUCCESS, response.data)
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        commit(INTEGRATION_UPDATE_FAILURE, errorMessage)
        return Promise.reject(error)
      })
  },
  deleteIntegration({ commit }, integrationId) {
    commit(INTEGRATION_DELETE_BEGIN)
    return integrationAPI
      .deleteIntegration(integrationId)
      .then(() => {
        commit(INTEGRATION_DELETE_SUCCESS, integrationId)
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error)
        commit(INTEGRATION_DELETE_FAILURE, errorMessage)
        return Promise.reject(error)
      })
  },
  setTargetIntegration({ commit }, uuid) {
    commit(SET_TARGET_INTEGRATION, uuid)
  },
  /**
   * Initiates the OAuth flow for a given integration type.
   * @param {Object} payload - Contains widgetId and integrationType.
   * @param {String} payload.widgetId - The widget ID.
   * @param {String} payload.integrationType - The integration type (e.g., 'MAILCHIMP').
   * @returns {Promise}
   * @param widgetId
   * @param integrationType
   */
  initiateOAuthIntegration({ dispatch }, { widgetId, integrationType }) {
    return integrationAPI
      .getOAuthAuthUrl({ widget: widgetId, integration: integrationType })
      .then((response) => {
        const authUrl = response.data.authorization_url

        // Popup window dimensions and position
        const width = 600
        const height = 700
        const left = (window.screen.width / 2) - (width / 2)
        const top = (window.screen.height / 2) - (height / 2)

        const popup = window.open(
          authUrl,
          `${integrationType} OAuth`,
          `width=${width},height=${height},top=${top},left=${left}`,
        )

        if (!popup) {
          dispatch(
            'notification/showError',
            'Failed to open popup window. Please allow popups for this site.',
            { root: true },
          )
        }
      })
      .catch((error) => {
        dispatch(
          'notification/showError',
          `Failed to initiate OAuth for ${integrationType}.`,
          { root: true },
        )
        return Promise.reject(error)
      })
  },
  /**
   * Handles OAuth callback messages.
   * @param {Object} payload - Contains message data from OAuth popup.
   * @param {Object} payload.message - The message data containing type, integrationType, and integrationId.
   */
  handleOAuthCallback({ commit, dispatch, state }, message) {
    // Generic handler for OAuth callbacks
    const { type, integrationType, integrationId } = message

    if (type && integrationType && integrationId) {
      dispatch('listIntegrations')
        .then(() => {
          commit(SET_TARGET_INTEGRATION, integrationId)
          // Additional logic can be added here if needed
        })
        .catch((error) => {
          dispatch(
            'notification/showError',
            'Failed to list integrations after OAuth.',
            { root: true },
          )
          console.error('Failed to list integrations:', error)
        })
    }
  },
  clearState({ commit }) {
    commit(INTEGRATION_CLEAR)
  },
}
